/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import i18n from '../i18n'

export function useCompany(companyId) {
    const [company, setCompany] = useState(null)
    const [members, setMembers] = useState([])
    const [loading, setLoading] = useState(true)

    const fetch = useCallback(async () => {
        if (!companyId) {
            setCompany(null)
            setMembers([])
            setLoading(false)
            return
        }

        // Parallel — was 2 sequential awaits. With ~100ms each that's 200ms
        // saved off the App-shell mount path that runs on every page load.
        const [companyRes, membersRes] = await Promise.all([
            kanbify.from('companies').select('*').eq('id', companyId).single(),
            kanbify
                .from('profiles')
                .select('id, full_name, email, role, points, level, streak_days, avatar_url, position, status, is_active, user_badges(badge_key)')
                .eq('company_id', companyId),
        ])

        setCompany(companyRes.data)
        setMembers(membersRes.data || [])
        setLoading(false)
    }, [companyId])

    useEffect(() => {
        fetch()
    }, [fetch])

    const update = async (updates) => {
        const { data } = await kanbify.from('companies').update(updates).eq('id', companyId).select().single()
        if (data) setCompany(data)
        return data
    }

    // Hard-delete a departing employee from EVERYWHERE (login, assignments,
    // history, KPI, comments — see admin_delete_user RPC). Irreversible.
    const deleteMember = async (userId) => {
        const { data, error } = await kanbify.rpc('admin_delete_user', { p_user_id: userId })
        if (error || data?.success === false) {
            return { success: false, error: error?.message || data?.error || i18n.t('company.deleteFailed') }
        }
        setMembers((current) => current.filter((member) => member.id !== userId))
        return { success: true }
    }

    const updateMemberRole = async (userId, newRole) => {
        await kanbify.from('profiles').update({ role: newRole }).eq('id', userId)
        setMembers((current) => current.map((member) => (
            member.id === userId ? { ...member, role: newRole } : member
        )))
    }

    return { company, members, loading, fetch, update, deleteMember, updateMemberRole }
}

export function useBoardMembers(boardId) {
    const [members, setMembers] = useState([])

    const fetch = useCallback(async () => {
        if (!boardId) {
            setMembers([])
            return
        }

        const { data } = await kanbify
            .from('board_members')
            .select('user_id, profiles(id, full_name, avatar_url, role, is_active)')
            .eq('board_id', boardId)

        setMembers((data || []).map((row) => row.profiles).filter((profile) => profile?.is_active !== false))
    }, [boardId])

    useEffect(() => {
        fetch()
    }, [fetch])

    const setAllMembers = async (userIds) => {
        const { data, error } = await kanbify.rpc('admin_set_board_members', {
            p_board_id: boardId,
            p_user_ids: userIds,
        })

        if (data?.success) await fetch()
        return data?.success ? { success: true } : { success: false, error: data?.error || error?.message }
    }

    return { members, fetch, setAllMembers }
}

export async function adminUpdateUser(userId, { role, companyId } = {}) {
    const { data, error } = await kanbify.rpc('admin_update_user', {
        p_user_id: userId,
        p_role: role ?? null,
        p_company_id: companyId ?? null,
    })

    return data?.success ? { success: true } : { success: false, error: data?.error || error?.message }
}

export async function createCompany(name, ownerId) {
    const joinCode = Array.from(
        { length: 8 },
        () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)],
    ).join('')

    const { data, error } = await kanbify
        .from('companies')
        .insert([{ name, owner_id: ownerId, join_code: joinCode }])
        .select()
        .single()

    if (data) {
        await kanbify.from('profiles').update({ company_id: data.id, role: 'admin' }).eq('id', ownerId)
    }

    return { data, error }
}

export async function findCompanyByCode(code) {
    const { data, error } = await kanbify.rpc('find_company_by_code', { p_code: code })
    if (error || !data?.length) return null
    return data[0]
}
